import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Container, Form, Spinner } from 'react-bootstrap';
import axios from '../libs/httpClient';
import { host } from '../libs/webRTC';

const lists = [
    ['knownVersions', 'Известные выпущенные версии', 'Версии, которые действительно распространялись. Номера вне списка считаются неизвестными.'],
    ['deprecatedVersions', 'Устаревшие версии', 'Дополнительные устаревшие версии. Все версии ниже актуальной также считаются устаревшими.'],
    ['unknownVersions', 'Несуществующие версии', 'Номера, которые не выпускались. При заполненном списке известных все номера вне него также считаются неизвестными.'],
    ['blockedVersions', 'Неподдерживаемые версии', 'Версии с известными проблемами. Все версии ниже минимальной также считаются неподдерживаемыми.']
];
const flags = [
    ['blockOutdated', 'Блокировать устаревшие версии'],
    ['blockUnsupported', 'Блокировать неподдерживаемые версии'],
    ['blockUnknown', 'Блокировать неизвестные / несуществующие версии'],
    ['requireVersion', 'Блокировать плагины, которые не передают версию']
];
const splitVersions = value => value.split(/[\s,;]+/).filter(Boolean);
export default function PluginSettings() {
    const [form, setForm] = useState(null);
    const [revision, setRevision] = useState(0);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const mounted = useRef(true);
    const apply = useCallback(data => {
        const next = { ...data.policy };
        lists.forEach(([key]) => { next[key] = (next[key] || []).join('\n'); });
        setForm(next); setRevision(data.revision); setUpdatedAt(data.updatedAt);
    }, []);
    const load = useCallback(async () => {
        setBusy(true); setError(''); setSaved(false);
        try {
            const { data } = await axios.get(`${host}/pluginSettings`);
            if (mounted.current) apply(data);
        } catch (failure) { if (mounted.current) setError(failure.response?.data?.error || 'Не удалось загрузить настройки.'); }
        finally { if (mounted.current) setBusy(false); }
    }, [apply]);
    useEffect(() => { mounted.current = true; load(); return () => { mounted.current = false; }; }, [load]);
    const change = (key, value) => { setForm(current => ({ ...current, [key]: value })); setSaved(false); };
    const save = async event => {
        event.preventDefault();
        if (busy) return;
        setBusy(true); setError(''); setSaved(false);
        const policy = { ...form };
        for (const key of ['latestVersion', 'minimumVersion', 'downloadUrl']) policy[key] = (policy[key] || '').trim() || null;
        lists.forEach(([key]) => { policy[key] = splitVersions(policy[key]); });
        try {
            const { data } = await axios.put(`${host}/pluginSettings`, { revision, policy });
            if (mounted.current) { apply(data); setSaved(true); }
        } catch (failure) {
            if (mounted.current) setError(failure.response?.data?.error || 'Не удалось сохранить настройки. Изменения не подтверждены.');
        } finally { if (mounted.current) setBusy(false); }
    };
    return <Container className="py-4" style={{ maxWidth: 900 }}>
        <h1>Настройки плагина</h1>
        <p>Правила применяются к началу новых записей. Сохранение и отправка уже записанных видео остаются доступны.</p>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        {saved && <Alert variant="success" role="status">Настройки сохранены и применяются к новым записям.</Alert>}
        <Button variant="outline-secondary" onClick={load} disabled={busy} className="mb-3">Загрузить актуальные настройки</Button>
        {busy && !form && <Spinner animation="border" role="status"><span className="sr-only">Загрузка</span></Spinner>}
        {form && <Form onSubmit={save}>
            <fieldset disabled={busy}>
                <Form.Group controlId="plugin-latestVersion">
                    <Form.Label>Актуальная версия</Form.Label>
                    <Form.Control value={form.latestVersion || ''} onChange={e => change('latestVersion', e.target.value)} maxLength={23} placeholder="Например, 3.2.0" />
                    <Form.Text>Укажите версию доступного студентам архива. Добавьте её в список известных версий.</Form.Text>
                </Form.Group>
                <Form.Group controlId="plugin-minimumVersion">
                    <Form.Label>Минимальная поддерживаемая версия</Form.Label>
                    <Form.Control value={form.minimumVersion || ''} onChange={e => change('minimumVersion', e.target.value)} maxLength={23} />
                    <Form.Text>Пустое поле отключает нижнюю границу.</Form.Text>
                </Form.Group>
                <Form.Group controlId="plugin-downloadUrl">
                    <Form.Label>Ссылка на архив обновления</Form.Label>
                    <Form.Control type="url" value={form.downloadUrl || ''} onChange={e => change('downloadUrl', e.target.value)} maxLength={2048} placeholder="https://…" />
                    <Form.Text>Необязательно. Только HTTPS.</Form.Text>
                </Form.Group>
                {lists.map(([key, label, description]) => <Form.Group controlId={'plugin-' + key} key={key}>
                    <Form.Label>{label}</Form.Label>
                    <Form.Control as="textarea" rows={3} value={form[key]} onChange={e => change(key, e.target.value)} maxLength={5000} />
                    <Form.Text>{description} Разделяйте номера запятыми или переносами строк.</Form.Text>
                </Form.Group>)}
                {flags.map(([key, label]) => <Form.Check className="mb-2" key={key} id={'plugin-' + key} type="checkbox" label={label}
                    checked={form[key]} onChange={e => change(key, e.target.checked)} />)}
                <p className="text-muted">Если блокировка категории выключена, плагин показывает предупреждение. При пересечении категорий достаточно одного включённого запрета.</p>
                <Button type="submit" disabled={busy}>{busy ? 'Сохранение…' : 'Сохранить настройки'}</Button>
                {updatedAt && <p className="text-muted mt-3">Последнее изменение: {new Date(updatedAt).toLocaleString('ru-RU')}</p>}
            </fieldset>
        </Form>}
    </Container>;
}
